<template>
  <div v-if="company && materials && pricesForCompany && availableCampaigns">
    <v-card
      border
      class="px-2 pt-2 px-md-4"
    >
      <v-tabs
        v-model="tab"
        show-arrows
        height="40"
      >
        <v-tab
          v-for="tabItem in availableTabs"
          :key="tabItem.value"
          :value="tabItem.value"
        >
          {{ $t(tabItem.label) }} ({{ getCount(tabItem) }})
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-window
        v-model="tab"
        class="py-4 transparent"
      >
        <v-window-item value="tools">
          <ToolsTab
            :company="company"
            :landingpages="tools"
            :get-materials="getNonCampaignMaterialsForLandinpage"
            :light-materials-count-by-topic-id="lightMaterialsCountByTopicId"
            :bookable-lps="bookableLps('tools')"
            :book-landingpage="bookLandingpage"
            :is-booking-blocked="isBookingBlocked"
            :price="price('landingpage')"
          />
        </v-window-item>

        <v-window-item value="guides">
          <DownloadTab
            type="GUIDE"
            :company="company"
            :price="price('landingpage')"
            :landingpages="guides"
            :bookable-lps="bookableLps('guides')"
            :is-booking-blocked="isBookingBlocked"
            :get-materials="getNonCampaignMaterialsForLandinpage"
            :book-landingpage="bookLandingpage"
          />
        </v-window-item>

        <v-window-item
          v-if="showChecklistTab"
          value="checklists"
        >
          <DownloadTab
            type="CHECKLIST"
            :company="company"
            :price="price('checklist')"
            :landingpages="checklists"
            :bookable-lps="bookableLps('checklists')"
            :is-booking-blocked="isBookingBlocked"
            :get-materials="getNonCampaignMaterialsForLandinpage"
            :book-landingpage="bookLandingpage"
          />
        </v-window-item>

        <v-window-item
          v-if="$features.feature(featureNames.CAMPAIGN)?.isActive"
          value="campaigns"
        >
          <CampaignTab
            :price="price('campaign')"
            :company="company"
            :campaigns="realCampaigns"
            :booked-campaigns="bookedCampaigns"
            :landingpages="landingpages"
            :landingpage-templates="landingpageTemplates"
            :is-booking-blocked="isBookingBlocked"
            :light-campaigns="lightCampaigns"
          />
        </v-window-item>

        <v-window-item
          v-if="showTemplateTab"
          value="templates"
        >
          <StaticTab
            :company="company"
            :campaigns="staticCampaigns"
          />
        </v-window-item>

        <v-window-item
          v-if="$features.feature(featureNames.NEWSFLASH)?.isActive"
          value="newsflash"
        >
          <CampaignTab
            :price="price('campaign')"
            :company="company"
            :campaigns="newsflashCampaigns"
            :booked-campaigns="bookedCampaigns"
            :landingpages="landingpages"
            :landingpage-templates="landingpageTemplates"
            :is-booking-blocked="isBookingBlocked"
            :light-campaigns="lightCampaigns"
          />
        </v-window-item>
      </v-window>
    </v-card>
  </div>
</template>

<script>
import featureNames from '@/lib/featureNames'
import { isLightUser } from '@/lib/productTypes'
import { TOOLS } from '@/lib/tools'
import brandingMixin from '@/mixins/branding'
import feature from '@/mixins/feature'
import BOOKED_CAMPAIGNS from '@/queries/BookedCampaigns.gql'
import LANDINGPAGE_TEMPLATES_FOR_COMPANY from '@/queries/LandingpageTemplatesForCompany.gql'

import PRICES from '../../queries/PricesForCompany.gql'
import GET_AD_MATERIALS from '../contentCreator/creator/queries/getAdMaterials.gql'
import AVAILABLE_CAMPAIGNS from './AvailableCampaigns.gql'
import BOOK_LANDINGPAGE from './BookLandingpage.gql'
import CampaignTab from './campaigns/CampaignTab.vue'
import CampaignType from './campaigns/enums/CampaignTypes'
import GET_LIGHT_CAMPAIGN_MATERIALS from './campaigns/GetLightCampaignMaterials.gql'
import DownloadTab from './download/DownloadTab.vue'
import LANDINGPAGES from './Landingpages.gql'
import MATERIALS from './materials.gql'
import StaticTab from './static/StaticTab.vue'
import ToolsTab from './tools/ToolsTab.vue'

const LP_TYPE_MAP = {
  tools: [
    'VALUATION',
    'RETURN_CALL',
    'LIFE_ANNUITY',
    'PARTIAL_SALE',
    'PLOT_VALUATION',
    'QUIZZARD',
    'PROPERTY_REQUEST',
  ],
  guides: ['GUIDE'],
  checklists: ['CHECKLIST'],
}

export default {
  name: 'Landingpages',

  components: {
    ToolsTab,
    DownloadTab,
    CampaignTab,
    StaticTab,
  },

  mixins: [brandingMixin, feature],

  data: () => ({
    company: null,
    materials: [],
    landingpageTemplates: [],
    tab: null,
    fetchLandingpageInterval: null,
    fetchLandingpageTimeout: null,
    availableCampaigns: [],
    bookedCampaigns: [],
  }),

  computed: {
    availableTabs() {
      const tabs = [
        { value: 'tools', label: 'landingpages.labels.tools' },
        { value: 'guides', label: 'landingpages.labels.guides' },
      ]

      if (this.showChecklistTab) {
        tabs.push({ value: 'checklists', label: 'landingpages.labels.checklists' })
      }

      if (this.$features.feature(featureNames.CAMPAIGN)?.isActive) {
        tabs.push({ value: 'campaigns', label: 'landingpages.labels.campaigns' })
      }

      if (this.showTemplateTab) {
        tabs.push({ value: 'templates', label: 'landingpages.labels.templates' })
      }

      if (this.$features.feature(featureNames.NEWSFLASH)?.isActive) {
        tabs.push({ value: 'newsflash', label: 'landingpages.labels.newsflash' })
      }

      return tabs
    },

    availableProductReadableIds() {
      return ['landingpage', 'checklist', `campaign-${this.company?.productType?.toLowerCase()}`]
    },

    landingpages() {
      return (this.company.landingpages || [])
        .filter(({ slug }) => this.$features.feature('lp-' + slug)?.isActive)
        .sort((a, b) => new Date(b.template.createdAt) - new Date(a.template.createdAt))
    },

    isBookingBlocked() {
      if (!this.$features.feature(featureNames.CONTRACT)?.isActive) {
        return false
      }
      const currentDate = new Date()
      if (!this.company.currentContract.endDate) {
        return false
      }
      const endDate = new Date(this.company.currentContract.endDate)
      const isInNext2Months = Math.floor(Math.abs(endDate - currentDate) / 86400000) < 60
      return this.company.state.value.ACTIVE === 'CANCELLED' && isInNext2Months
    },

    showTemplateTab() {
      return this.$features.feature(featureNames.STATIC_CAMPAIGN)?.isActive
    },

    showChecklistTab() {
      return this.$features.feature(featureNames.LANDINGPAGE)?.config?.hasChecklists
    },

    tools() {
      return this.filterType(this.landingpages, LP_TYPE_MAP.tools)
        .map((lp) => ({ ...lp, ...TOOLS.find((tool) => tool.type === lp.type) }))
        .filter((lp) => lp.order)
        .sort((a, b) => a.order - b.order)
    },

    guides() {
      return this.filterType(this.landingpages, LP_TYPE_MAP.guides)
    },

    checklists() {
      return this.filterType(this.landingpages, LP_TYPE_MAP.checklists)
    },

    realCampaigns() {
      return this.availableCampaigns.filter((campaign) => campaign.type === CampaignType.DEFAULT)
    },

    staticCampaigns() {
      return this.availableCampaigns.filter((campaign) => campaign.type === CampaignType.STATIC)
    },

    newsflashCampaigns() {
      return this.availableCampaigns.filter((campaign) => campaign.type === CampaignType.NEWSFLASH)
    },

    lightMaterialsCountByTopicId() {
      const materials = this.lightAdMaterials || []
      const map = {}
      materials.forEach((m) => {
        if (m.topicId) {
          map[m.topicId] = (map[m.topicId] || 0) + 1
        }
      })
      return map
    },

    lightCampaigns() {
      return this.lightCampaignsData || []
    },

    toolTemplates() {
      return this.filterType(this.landingpageTemplates, LP_TYPE_MAP.tools)
    },

    guideTemplates() {
      return this.filterType(this.landingpageTemplates, LP_TYPE_MAP.guides)
    },

    checklistTemplates() {
      return this.filterType(this.landingpageTemplates, LP_TYPE_MAP.checklists)
    },

    isLight() {
      return isLightUser(this.$auth.user)
    },
  },

  watch: {
    '$route.params.view': {
      immediate: true,
      handler(view) {
        if (view && this.availableTabs.some((tab) => tab.value === view)) {
          this.tab = view
        } else {
          this.navigateToTab(this.availableTabs[0].value)
        }
      },
    },

    tab(newTab) {
      if (newTab !== this.$route.params.view) {
        this.navigateToTab(newTab)
      }
    },
  },

  created() {
    const view = this.$route.params.view
    if (view && this.availableTabs.some((tab) => tab.value === view)) {
      this.tab = view
    } else {
      this.navigateToTab(this.availableTabs[0].value)
    }
  },

  unmounted() {
    if (this.fetchLandingpageInterval) {
      clearInterval(this.fetchLandingpageInterval)
    }
    if (this.fetchLandingpageTimeout) {
      clearTimeout(this.fetchLandingpageTimeout)
    }
  },

  apollo: {
    company: {
      query: LANDINGPAGES,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
    },
    pricesForCompany: {
      query: PRICES,
      variables() {
        return {
          input: {
            companyId: this.$auth.user.companyId,
            readableIds: this.availableProductReadableIds,
          },
        }
      },
    },
    materials: {
      query: MATERIALS,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
    },
    landingpageTemplates: {
      query: LANDINGPAGE_TEMPLATES_FOR_COMPANY,
      variables() {
        return {
          input: {
            companyId: this.$auth.user.companyId,
            onlyDefault: true,
          },
        }
      },
    },
    availableCampaigns: {
      query: AVAILABLE_CAMPAIGNS,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
    },
    bookedCampaigns: {
      query: BOOKED_CAMPAIGNS,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
    },
    lightAdMaterials: {
      query: GET_AD_MATERIALS,
      variables() {
        return {
          input: { itemsPerPage: 500 },
        }
      },
      skip() {
        return !this.isLight
      },
      update: (data) => data?.adMaterials?.adMaterials || [],
    },
    lightCampaignsData: {
      query: GET_LIGHT_CAMPAIGN_MATERIALS,
      skip() {
        return !this.isLight
      },
      update: (data) => data?.getLightCampaignMaterials || [],
    },
  },

  methods: {
    navigateToTab(tab) {
      this.$router.replace({
        name: 'landingpages-view',
        params: { view: tab },
      })
    },

    getCount(tabItem) {
      switch (tabItem.value) {
        case 'tools':
          return this.count(this.tools, 'tools')
        case 'guides':
          return this.count(this.guides, 'guides')
        case 'checklists':
          return this.count(this.checklists, 'checklists')
        case 'campaigns':
          return this.countForStaticLightCampaign()
        case 'templates':
          return this.staticCampaigns.length
        case 'newsflash':
          return this.newsflashCampaigns.length
        default:
          return '0/0'
      }
    },

    isCampaignAvailableForCountry({ landingpages }) {
      return (
        landingpages.length === 0 ||
        landingpages.some(({ country }) => country === this.company?.country)
      )
    },

    count(template, type) {
      const activeCount = template?.length || 0
      const bookableCount = this.bookableLps(type)?.length || 0
      return `${activeCount}/${activeCount + bookableCount}`
    },

    countForStaticLightCampaign() {
      const staticCampaignsForLight = this.isLight ? this.lightCampaigns.length : 0
      return `${this.bookedCampaigns.length + staticCampaignsForLight} / ${this.realCampaigns.length + staticCampaignsForLight}`
    },

    bookableLps(type) {
      return this.filterType(this.landingpageTemplates, LP_TYPE_MAP[type])
        .filter((lp) => this.isUpsellingEnabled(lp))
        .filter((lp) => !this.isLpExisting(lp))
    },

    isLpExisting({ slug }) {
      return this.landingpages.find((lp) => lp.slug === slug)
    },

    isUpsellingEnabled(lp) {
      return this.$features.feature(`lp-${lp.slug}`).isUpsellingEnabled
    },

    filterType(items, types) {
      return items.filter((lp) => types.includes(lp.type))
    },

    price(type) {
      return this.pricesForCompany.find((price) => price.readableId.includes(type))
    },

    getNonCampaignMaterialsForLandinpage(lpId) {
      return this.materials.filter(
        ({ landingpageId, type }) => landingpageId === lpId && type !== 'CAMPAIGN'
      )
    },

    startFetchLandingpageInterval() {
      // for 15 minutes fetch all LPs every minute
      const timeout = 1000 * 60 * 15
      this.fetchLandingpageInterval = setInterval(() => {
        this.$apollo.queries.company.refetch()
        this.$apollo.queries.landingpageTemplates.refetch()
        this.$apollo.queries.materials.refetch()
      }, 60000)

      this.fetchLandingpageTimeout = setTimeout(() => {
        clearInterval(this.fetchLandingpageInterval)
      }, timeout)
    },

    async bookLandingpage(type, slug) {
      try {
        await this.$apollo.mutate({
          mutation: BOOK_LANDINGPAGE,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              slug: slug,
              type,
            },
          },
        })
        this.startFetchLandingpageInterval()
      } catch (e) {
        throw new Error('GraphQL Error: ', e)
      }
    },
  },
}
</script>
